import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { canAddMoreAddresses, getMaxAddressCount } from '../../util/dataExtractors';
import { IconAdd, PrimaryButton, SecondaryButton, Modal } from '../../components';
import SavedAddressCard from '../SavedAddressCard/SavedAddressCard';

import css from './SavedAddressList.module.css';

const SavedAddressList = props => {
  const {
    addresses,
    currentUser,
    onAdd,
    onEdit,
    onDelete,
    onSetDefault,
    setDefaultInProgress,
    className,
    isLoading,
  } = props;

  const intl = useIntl();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  // Clear tracked ID when setDefault finishes
  useEffect(() => {
    if (!setDefaultInProgress) setSettingDefaultId(null);
  }, [setDefaultInProgress]);

  const handleSetDefault = addressId => {
    setSettingDefaultId(addressId);
    if (onSetDefault) onSetDefault(addressId);
  };

  const canAdd = canAddMoreAddresses(currentUser);
  const maxAddresses = getMaxAddressCount();
  const hasAddresses = addresses && addresses.length > 0;

  const handleDeleteClick = addressId => {
    setAddressToDelete(addressId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (addressToDelete && onDelete) {
      onDelete(addressToDelete);
    }
    setDeleteModalOpen(false);
    setAddressToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setAddressToDelete(null);
  };

  const classes = classNames(css.root, className);

  return (
    <div className={classes}>
      <div className={css.header}>
        <div className={css.titleWrapper}>
          <h2 className={css.title}>
            {intl.formatMessage({ id: 'SavedAddresses.title' })}
          </h2>
          <p className={css.subtitle}>
            {intl.formatMessage({ id: 'SavedAddresses.subtitle' })}
          </p>
        </div>

        {canAdd && (
          <SecondaryButton
            className={css.addButton}
            onClick={onAdd}
            disabled={isLoading}
          >
            <IconAdd className={css.addIcon} />
            {intl.formatMessage({ id: 'SavedAddresses.addNew' })}
          </SecondaryButton>
        )}
      </div>

      {!canAdd && hasAddresses && (
        <p className={css.maxReached}>
          {intl.formatMessage(
            { id: 'SavedAddresses.maxAddressesReached' },
            { max: maxAddresses }
          )}
        </p>
      )}

      {hasAddresses ? (
        <div className={css.grid}>
          {addresses.map((address, index) => (
            <SavedAddressCard
              key={address.id}
              address={address}
              index={index}
              onEdit={onEdit}
              onDelete={handleDeleteClick}
              onSetDefault={handleSetDefault}
              setDefaultInProgress={setDefaultInProgress && settingDefaultId === address.id}
            />
          ))}
        </div>
      ) : (
        <div className={css.emptyState}>
          <p className={css.emptyTitle}>
            {intl.formatMessage({ id: 'SavedAddresses.emptyState' })}
          </p>
          <p className={css.emptyDescription}>
            {intl.formatMessage({ id: 'SavedAddresses.emptyStateDescription' })}
          </p>
          <SecondaryButton
            className={css.emptyAddButton}
            onClick={onAdd}
            disabled={isLoading}
          >
            <IconAdd className={css.addIcon} />
            {intl.formatMessage({ id: 'SavedAddresses.addNew' })}
          </SecondaryButton>
        </div>
      )}

      <Modal
        id="DeleteAddressModal"
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onManageDisableScrolling={() => {}}
        containerClassName={css.modalContainer}
        usePortal
      >
        <div className={css.modalContent}>
          <h3 className={css.modalTitle}>
            {intl.formatMessage({ id: 'SavedAddresses.deleteConfirmTitle' })}
          </h3>
          <p className={css.modalMessage}>
            {intl.formatMessage({ id: 'SavedAddresses.deleteConfirmMessage' })}
          </p>
          <div className={css.modalActions}>
            <SecondaryButton type="button" onClick={handleCancelDelete}>
              {intl.formatMessage({ id: 'SavedAddresses.cancelButton' })}
            </SecondaryButton>
            <PrimaryButton
              type="button"
              className={css.deleteButton}
              onClick={handleConfirmDelete}
            >
              {intl.formatMessage({ id: 'SavedAddresses.deleteConfirmButton' })}
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

SavedAddressList.defaultProps = {
  addresses: [],
  currentUser: null,
  className: null,
  isLoading: false,
  onAdd: null,
  onEdit: null,
  onDelete: null,
  onSetDefault: null,
  setDefaultInProgress: false,
};

SavedAddressList.propTypes = {
  addresses: PropTypes.array,
  currentUser: PropTypes.object,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSetDefault: PropTypes.func,
  setDefaultInProgress: PropTypes.bool,
  className: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default SavedAddressList;
